import { useEffect, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { itemLabel } from '../../components/ItemName';
import Panel from '../../components/ui/Panel';
import Alert from '../../components/ui/Alert';
import EmptyState from '../../components/ui/EmptyState';
import { ListStack, ListRow } from '../../components/ui/ListRow';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import Tag from '../../components/ui/Tag';
import { CATEGORY } from '../../components/ui/categoryColors';

/**
 * Row shape mirrors the GET /api/stale-boxes envelope built in
 * firefleet-api/src/parts/receipt-boxes-router.js. Snake-case is kept on
 * purpose — the API response isn't transformed and rewriting it here would
 * mean keeping two shapes in sync.
 */
interface StaleBox {
  /** The item (receipt_items) that went stale. */
  id: string;
  /** Its box's sticker. */
  barcode: string;
  quantity_received: number;
  quantity_remaining: number;
  received_at: string;
  sku: string;
  description: string | null;
  /** From catalog_item_names, joined on part_catalog_id. */
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  family_key: string | null;
  family_label: string | null;
  stale_box_alert_days: number | null;
  first_flash_at: string;
  days_since_first_flash: number;
  missing_count: number;
}

/**
 * Phase 1b dashboard widget — surfaces receipt boxes that started flashing
 * but haven't been fully consumed past their family's
 * `stale_box_alert_days` threshold. Same query the (currently deferred)
 * daily Slack cron would run; lets manufacturing/ops/admin spot stale
 * inventory inline without waiting for the proactive ping.
 *
 * Visibility: manufacturing, operations, admin. Wired in moduleRegistry.js.
 */
const StaleBoxesModule: React.FC = () => {
  const api = useApi();
  const [rows, setRows] = useState<StaleBox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/stale-boxes', { silent: true })
      .then((data: StaleBox[]) => {
        if (cancelled) return;
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load stale boxes');
        setLoading(false);
      });
    // Drawer-pattern cancellation: dashboard modules can mount/unmount as
    // the user toggles widgets in edit mode. Guards against post-unmount
    // setState if the GET resolves after the user navigates away.
    return () => {
      cancelled = true;
    };
    // useApi() returns a fresh object literal each render; including it in
    // the dep array would re-fire the fetch on every parent re-render. Mount-
    // only fetch is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Panel label="Stale receipt boxes">
        <SkeletonGroup label="Loading stale boxes" className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </SkeletonGroup>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel label="Stale receipt boxes">
        <Alert variant="note" tone="error">{error}</Alert>
      </Panel>
    );
  }

  if (rows.length === 0) {
    return (
      <Panel label="Stale receipt boxes">
        <EmptyState variant="area">No stale receipt boxes</EmptyState>
      </Panel>
    );
  }

  return (
    <Panel
      title="Stale Receipt Boxes"
      titleStyle="section"
      accent="amber"
      lit
      level={3}
      actions={(
        <Tag size="sm" hex={CATEGORY.amber.ink.hex} role="status" aria-live="polite">
          {rows.length} {rows.length === 1 ? 'box' : 'boxes'}
        </Tag>
      )}
    >
      <ListStack
        label="Stale receipt boxes"
        on="panel"
        items={rows}
        getKey={(box) => box.id}
        renderRow={(box) => {
          // Threshold-overshoot ratio drives a subtle red/orange split: a
          // box 2× over its family threshold reads "critical" rather than
          // just "stale." Falls back to amber when the threshold is null.
          const threshold = box.stale_box_alert_days;
          const overshoot = threshold ? box.days_since_first_flash / threshold : 1;
          const isCritical = overshoot >= 2;
          const accent = (isCritical ? CATEGORY.red : CATEGORY.amber).ink.oklch;

          return (
            <ListRow
              to={`/receiving/item/${box.id}`}
              aria-label={`Open stale ${itemLabel(box)} in box ${box.barcode}, ${box.days_since_first_flash} days since first flash, ${box.missing_count} unaccounted`}
              title={itemLabel(box)}
              tone={isCritical ? 'danger' : 'warn'}
              meta={`${box.barcode}, ${box.family_label || 'no family'}, ${box.days_since_first_flash}d since first flash`}
              trailing={<span className="text-xs font-semibold tabular-nums" style={{ color: accent }}>{isCritical ? `Critical, ${box.missing_count} missing` : `${box.missing_count} missing`}</span>}
            />
          );
        }}
      />
    </Panel>
  );
};

export default StaleBoxesModule;
